import type { TarotCard } from '../types/tarot'

export const TAROT_DECK: TarotCard[] = [
  // ============================================================
  // MAJOR ARCANA
  // ============================================================
  {
    id: 'the-fool', name: 'The Fool', arcana: 'major', number: 0, element: 'air',
    keywords: { upright: ['new beginnings', 'innocence', 'spontaneity', 'free spirit'], reversed: ['recklessness', 'taken advantage of', 'inconsideration', 'naivety'] },
    planet: 'Uranus',
    description: 'A young traveler stands at the edge of a cliff, gazing upward with a white rose in one hand and a small bundle on a stick in the other, a small white dog at their heels.',
    imageSlug: 'the-fool',
  },
  {
    id: 'the-magician', name: 'The Magician', arcana: 'major', number: 1, element: 'air',
    keywords: { upright: ['manifestation', 'resourcefulness', 'power', 'inspired action'], reversed: ['manipulation', 'poor planning', 'untapped talents', 'deception'] },
    planet: 'Mercury',
    description: 'A robed figure stands before a table bearing a cup, sword, wand, and pentacle, one hand pointing to the sky and the other to the earth, an infinity symbol above their head.',
    imageSlug: 'the-magician',
  },
  {
    id: 'the-high-priestess', name: 'The High Priestess', arcana: 'major', number: 2, element: 'water',
    keywords: { upright: ['intuition', 'sacred knowledge', 'divine feminine', 'subconscious'], reversed: ['secrets', 'withdrawal', 'silence', 'disconnected from intuition'] },
    planet: 'Moon',
    description: 'A serene woman sits between two pillars marked B and J, a crescent moon at her feet and a scroll in her lap, a veil of pomegranates behind her.',
    imageSlug: 'the-high-priestess',
  },
  {
    id: 'the-empress', name: 'The Empress', arcana: 'major', number: 3, element: 'earth',
    keywords: { upright: ['femininity', 'beauty', 'nature', 'abundance'], reversed: ['creative block', 'dependence', 'emptiness', 'smothering'] },
    planet: 'Venus',
    description: 'A crowned woman reclines on cushions in a lush garden, wearing a robe of pomegranates, a heart-shaped shield with the Venus symbol at her side, wheat at her feet.',
    imageSlug: 'the-empress',
  },
  {
    id: 'the-emperor', name: 'The Emperor', arcana: 'major', number: 4, element: 'fire',
    keywords: { upright: ['authority', 'structure', 'control', 'fatherhood'], reversed: ['tyranny', 'rigidity', 'coldness', 'domination'] },
    zodiacSign: 'Aries',
    description: 'A stern figure sits on a stone throne carved with ram heads, holding an ankh scepter and an orb, armored beneath red robes, barren mountains behind.',
    imageSlug: 'the-emperor',
  },
  {
    id: 'the-hierophant', name: 'The Hierophant', arcana: 'major', number: 5, element: 'earth',
    keywords: { upright: ['tradition', 'conformity', 'morality', 'spiritual wisdom'], reversed: ['rebellion', 'subversiveness', 'new approaches', 'personal beliefs'] },
    zodiacSign: 'Taurus',
    description: 'A religious figure sits between two grey pillars wearing a triple crown and holding a triple cross scepter, two acolytes kneeling before him, crossed keys at his feet.',
    imageSlug: 'the-hierophant',
  },
  {
    id: 'the-lovers', name: 'The Lovers', arcana: 'major', number: 6, element: 'air',
    keywords: { upright: ['love', 'harmony', 'relationships', 'values alignment'], reversed: ['self-love', 'disharmony', 'imbalance', 'misalignment'] },
    zodiacSign: 'Gemini',
    description: 'A naked man and woman stand beneath a great angel with outstretched arms, the Tree of Life behind the woman and the Tree of Knowledge behind the man.',
    imageSlug: 'the-lovers',
  },
  {
    id: 'the-chariot', name: 'The Chariot', arcana: 'major', number: 7, element: 'water',
    keywords: { upright: ['control', 'willpower', 'success', 'determination'], reversed: ['self-discipline', 'opposition', 'lack of direction', 'aggression'] },
    zodiacSign: 'Cancer',
    description: 'An armored warrior stands in a chariot pulled by one black and one white sphinx, a canopy of stars overhead, a city wall behind.',
    imageSlug: 'the-chariot',
  },
  {
    id: 'strength', name: 'Strength', arcana: 'major', number: 8, element: 'fire',
    keywords: { upright: ['courage', 'persuasion', 'influence', 'compassion'], reversed: ['inner doubt', 'weakness', 'insecurity', 'raw emotion'] },
    zodiacSign: 'Leo',
    description: 'A woman gently closes the jaws of a lion, an infinity symbol above her head, wearing a white robe and garland of flowers, showing calm mastery over the beast.',
    imageSlug: 'strength',
  },
  {
    id: 'the-hermit', name: 'The Hermit', arcana: 'major', number: 9, element: 'earth',
    keywords: { upright: ['soul-searching', 'introspection', 'being alone', 'inner guidance'], reversed: ['isolation', 'loneliness', 'withdrawal', 'lost'] },
    zodiacSign: 'Virgo',
    description: 'An old robed figure stands alone atop a snowy mountain peak, holding a lantern containing a six-pointed star in one hand and a long staff in the other.',
    imageSlug: 'the-hermit',
  },
  {
    id: 'wheel-of-fortune', name: 'Wheel of Fortune', arcana: 'major', number: 10, element: 'fire',
    keywords: { upright: ['good luck', 'karma', 'life cycles', 'destiny'], reversed: ['bad luck', 'resistance to change', 'breaking cycles', 'upheaval'] },
    planet: 'Jupiter',
    description: 'A great wheel inscribed with alchemical symbols floats in the sky, a sphinx atop, a snake descending left, Anubis rising right, four winged creatures in corners.',
    imageSlug: 'wheel-of-fortune',
  },
  {
    id: 'justice', name: 'Justice', arcana: 'major', number: 11, element: 'air',
    keywords: { upright: ['justice', 'fairness', 'truth', 'cause and effect'], reversed: ['unfairness', 'lack of accountability', 'dishonesty', 'bias'] },
    zodiacSign: 'Libra',
    description: 'A robed figure sits between two pillars holding a sword upright in one hand and balanced scales in the other, wearing a crown with a small square jewel.',
    imageSlug: 'justice',
  },
  {
    id: 'the-hanged-man', name: 'The Hanged Man', arcana: 'major', number: 12, element: 'water',
    keywords: { upright: ['pause', 'surrender', 'letting go', 'new perspectives'], reversed: ['delays', 'resistance', 'stalling', 'indecision'] },
    planet: 'Neptune',
    description: 'A man hangs upside-down from a living T-shaped tree by one foot, the other leg crossed behind, a halo of golden light around his head, expression serene.',
    imageSlug: 'the-hanged-man',
  },
  {
    id: 'death', name: 'Death', arcana: 'major', number: 13, element: 'water',
    keywords: { upright: ['endings', 'change', 'transformation', 'transition'], reversed: ['resistance to change', 'personal transformation', 'inner purging', 'stagnation'] },
    zodiacSign: 'Scorpio',
    description: 'A skeleton in black armor rides a white horse carrying a black flag with a white mystic rose, figures of all ages kneel before it, a rising sun between two towers.',
    imageSlug: 'death',
  },
  {
    id: 'temperance', name: 'Temperance', arcana: 'major', number: 14, element: 'fire',
    keywords: { upright: ['balance', 'moderation', 'patience', 'purpose'], reversed: ['imbalance', 'excess', 'self-healing', 'realignment'] },
    zodiacSign: 'Sagittarius',
    description: 'A winged angel stands with one foot on land and one in water, pouring liquid between two cups, a path leading to a glowing crown on the horizon.',
    imageSlug: 'temperance',
  },
  {
    id: 'the-devil', name: 'The Devil', arcana: 'major', number: 15, element: 'earth',
    keywords: { upright: ['shadow self', 'attachment', 'addiction', 'restriction'], reversed: ['releasing limiting beliefs', 'exploring dark thoughts', 'detachment', 'freedom'] },
    zodiacSign: 'Capricorn',
    description: 'A horned devil crouches on a pedestal, a reversed pentagram above, a chained naked man and woman below, their chains loose enough to remove.',
    imageSlug: 'the-devil',
  },
  {
    id: 'the-tower', name: 'The Tower', arcana: 'major', number: 16, element: 'fire',
    keywords: { upright: ['sudden change', 'upheaval', 'chaos', 'revelation'], reversed: ['fear of change', 'averting disaster', 'personal transformation', 'narrow escape'] },
    planet: 'Mars',
    description: 'A tall stone tower struck by lightning, its crown blown off in flames, two figures falling from windows against a dark sky, sparks and debris raining down.',
    imageSlug: 'the-tower',
  },
  {
    id: 'the-star', name: 'The Star', arcana: 'major', number: 17, element: 'air',
    keywords: { upright: ['hope', 'faith', 'purpose', 'renewal'], reversed: ['lack of faith', 'despair', 'disconnection', 'insecurity'] },
    zodiacSign: 'Aquarius',
    description: 'A naked woman kneels by a pool beneath eight stars, pouring water from two pitchers onto the land and into the pool, a bird perched in a tree behind.',
    imageSlug: 'the-star',
  },
  {
    id: 'the-moon', name: 'The Moon', arcana: 'major', number: 18, element: 'water',
    keywords: { upright: ['illusion', 'fear', 'anxiety', 'subconscious'], reversed: ['release of fear', 'repressed emotion', 'inner confusion', 'clarity emerging'] },
    zodiacSign: 'Pisces',
    description: 'A full moon with a face shines between two towers, a dog and wolf howl, a crayfish crawls from a pool onto a winding path leading into distant mountains.',
    imageSlug: 'the-moon',
  },
  {
    id: 'the-sun', name: 'The Sun', arcana: 'major', number: 19, element: 'fire',
    keywords: { upright: ['positivity', 'warmth', 'success', 'vitality'], reversed: ['inner child', 'feeling down', 'overly optimistic', 'temporary depression'] },
    planet: 'Sun',
    description: 'A joyful naked child rides a white horse beneath a blazing sun with a face, arms spread wide, sunflowers growing tall behind a grey wall.',
    imageSlug: 'the-sun',
  },
  {
    id: 'judgement', name: 'Judgement', arcana: 'major', number: 20, element: 'fire',
    keywords: { upright: ['judgement', 'rebirth', 'inner calling', 'absolution'], reversed: ['self-doubt', 'inner critic', 'ignoring the call', 'poor judgement'] },
    planet: 'Pluto',
    description: 'An angel blows a great trumpet from clouds, figures rise from coffins with arms outstretched, grey mountains and a river in the background.',
    imageSlug: 'judgement',
  },
  {
    id: 'the-world', name: 'The World', arcana: 'major', number: 21, element: 'earth',
    keywords: { upright: ['completion', 'integration', 'accomplishment', 'travel'], reversed: ['seeking personal closure', 'shortcuts', 'delays', 'incomplete'] },
    planet: 'Saturn',
    description: 'A dancing figure wrapped in purple cloth holds two wands inside a great laurel wreath, four creatures in corners: angel, eagle, bull, and lion.',
    imageSlug: 'the-world',
  },

  // ============================================================
  // MINOR ARCANA - WANDS (Fire)
  // ============================================================
  {
    id: 'ace-of-wands', name: 'Ace of Wands', arcana: 'minor', suit: 'wands', number: 1, element: 'fire',
    keywords: { upright: ['inspiration', 'new opportunity', 'growth', 'potential'], reversed: ['delays', 'lack of motivation', 'creative block', 'missed opportunity'] },
    description: 'A hand emerges from a cloud grasping a sprouting wand with fresh leaves, three leaves floating toward a distant castle and green rolling hills.',
    imageSlug: 'ace-of-wands',
  },
  {
    id: 'two-of-wands', name: 'Two of Wands', arcana: 'minor', suit: 'wands', number: 2, element: 'fire',
    keywords: { upright: ['future planning', 'progress', 'decisions', 'discovery'], reversed: ['fear of unknown', 'lack of planning', 'playing it safe', 'indecision'] },
    planet: 'Mars', zodiacSign: 'Aries',
    description: 'A man in a red robe stands on a castle wall holding a globe and a wand, gazing over a vast landscape toward the sea, a second wand mounted to the wall.',
    imageSlug: 'two-of-wands',
  },
  {
    id: 'three-of-wands', name: 'Three of Wands', arcana: 'minor', suit: 'wands', number: 3, element: 'fire',
    keywords: { upright: ['expansion', 'foresight', 'overseas opportunities', 'progress'], reversed: ['obstacles', 'delays', 'frustration', 'lack of foresight'] },
    planet: 'Sun', zodiacSign: 'Aries',
    description: 'A merchant stands on a cliff watching ships sail toward the horizon, holding one wand with two others planted beside him.',
    imageSlug: 'three-of-wands',
  },
  {
    id: 'four-of-wands', name: 'Four of Wands', arcana: 'minor', suit: 'wands', number: 4, element: 'fire',
    keywords: { upright: ['celebration', 'joy', 'harmony', 'homecoming'], reversed: ['personal celebration', 'inner harmony', 'conflict', 'transition'] },
    planet: 'Venus', zodiacSign: 'Aries',
    description: 'Four wands form a canopy of flowers and garlands, two figures dance in the foreground, a castle and welcoming crowd in the background.',
    imageSlug: 'four-of-wands',
  },
  {
    id: 'five-of-wands', name: 'Five of Wands', arcana: 'minor', suit: 'wands', number: 5, element: 'fire',
    keywords: { upright: ['conflict', 'disagreements', 'competition', 'tension'], reversed: ['inner conflict', 'conflict avoidance', 'compromise', 'release of tension'] },
    planet: 'Saturn', zodiacSign: 'Leo',
    description: 'Five young men brandish wands in a chaotic struggle, each battling the others yet none landing a blow, a scene of competition and creative tension.',
    imageSlug: 'five-of-wands',
  },
  {
    id: 'six-of-wands', name: 'Six of Wands', arcana: 'minor', suit: 'wands', number: 6, element: 'fire',
    keywords: { upright: ['success', 'public recognition', 'progress', 'self-confidence'], reversed: ['excess pride', 'lack of recognition', 'fall from grace', 'egotism'] },
    planet: 'Jupiter', zodiacSign: 'Leo',
    description: 'A victorious rider on a white horse parades through a crowd, holding a wand topped with a laurel wreath, five supporters raising their wands.',
    imageSlug: 'six-of-wands',
  },
  {
    id: 'seven-of-wands', name: 'Seven of Wands', arcana: 'minor', suit: 'wands', number: 7, element: 'fire',
    keywords: { upright: ['challenge', 'competition', 'protection', 'perseverance'], reversed: ['exhaustion', 'giving up', 'overwhelmed', 'yielding'] },
    planet: 'Mars', zodiacSign: 'Leo',
    description: 'A man stands on a hilltop wielding a wand defensively against six wands rising from below, holding the high ground with determination.',
    imageSlug: 'seven-of-wands',
  },
  {
    id: 'eight-of-wands', name: 'Eight of Wands', arcana: 'minor', suit: 'wands', number: 8, element: 'fire',
    keywords: { upright: ['movement', 'fast paced change', 'action', 'alignment'], reversed: ['delays', 'frustration', 'resisting change', 'internal alignment'] },
    planet: 'Mercury', zodiacSign: 'Sagittarius',
    description: 'Eight wands fly through a clear blue sky in diagonal formation, soaring over a river and gentle green landscape, suggesting swift momentum.',
    imageSlug: 'eight-of-wands',
  },
  {
    id: 'nine-of-wands', name: 'Nine of Wands', arcana: 'minor', suit: 'wands', number: 9, element: 'fire',
    keywords: { upright: ['resilience', 'courage', 'persistence', 'last stand'], reversed: ['exhaustion', 'fatigue', 'giving up', 'overwhelm'] },
    planet: 'Moon', zodiacSign: 'Sagittarius',
    description: 'A bandaged and weary figure leans on a wand, standing guard before eight wands planted behind, watchful and wounded but unbowed.',
    imageSlug: 'nine-of-wands',
  },
  {
    id: 'ten-of-wands', name: 'Ten of Wands', arcana: 'minor', suit: 'wands', number: 10, element: 'fire',
    keywords: { upright: ['burden', 'responsibility', 'hard work', 'overcommitment'], reversed: ['release', 'delegation', 'breakdown', 'unsustainable effort'] },
    planet: 'Saturn', zodiacSign: 'Sagittarius',
    description: 'A figure struggles to carry ten heavy wands, hunched forward under the weight walking toward a distant town, unable to see the path ahead.',
    imageSlug: 'ten-of-wands',
  },
  {
    id: 'page-of-wands', name: 'Page of Wands', arcana: 'minor', suit: 'wands', number: 11, element: 'fire',
    keywords: { upright: ['enthusiasm', 'exploration', 'discovery', 'free spirit'], reversed: ['impatience', 'lack of direction', 'procrastination', 'hasty decisions'] },
    description: 'A youthful figure stands in a barren landscape holding a tall wand, gazing at its sprouting leaves with excitement, wearing a tunic with salamanders.',
    imageSlug: 'page-of-wands',
  },
  {
    id: 'knight-of-wands', name: 'Knight of Wands', arcana: 'minor', suit: 'wands', number: 12, element: 'fire',
    keywords: { upright: ['action', 'adventure', 'passion', 'fearlessness'], reversed: ['impulsiveness', 'recklessness', 'haste', 'scattered energy'] },
    description: 'An armored knight charges on a rearing horse, brandishing a sprouting wand, three pyramids in the desert behind, plumes of fire on the helmet.',
    imageSlug: 'knight-of-wands',
  },
  {
    id: 'queen-of-wands', name: 'Queen of Wands', arcana: 'minor', suit: 'wands', number: 13, element: 'fire',
    keywords: { upright: ['confidence', 'independence', 'warmth', 'determination'], reversed: ['selfishness', 'jealousy', 'demanding', 'insecurity'] },
    description: 'A confident queen sits on a throne with lions and sunflowers, holding a wand and a sunflower, a black cat at her feet.',
    imageSlug: 'queen-of-wands',
  },
  {
    id: 'king-of-wands', name: 'King of Wands', arcana: 'minor', suit: 'wands', number: 14, element: 'fire',
    keywords: { upright: ['visionary leadership', 'boldness', 'entrepreneurship', 'charisma'], reversed: ['impulsiveness', 'ruthlessness', 'high expectations', 'overbearing'] },
    description: 'A king sits on a throne carved with lions and salamanders, holding a sprouting wand, looking forward with determination, a salamander at his feet.',
    imageSlug: 'king-of-wands',
  },

  // ============================================================
  // MINOR ARCANA - CUPS (Water)
  // ============================================================
  {
    id: 'ace-of-cups', name: 'Ace of Cups', arcana: 'minor', suit: 'cups', number: 1, element: 'water',
    keywords: { upright: ['new love', 'compassion', 'emotional fulfillment', 'creativity'], reversed: ['emotional loss', 'blocked feelings', 'emptiness', 'repression'] },
    description: 'A hand emerges from a cloud holding a golden chalice overflowing with five streams of water, a dove descending, above a sea of lotus blossoms.',
    imageSlug: 'ace-of-cups',
  },
  {
    id: 'two-of-cups', name: 'Two of Cups', arcana: 'minor', suit: 'cups', number: 2, element: 'water',
    keywords: { upright: ['partnership', 'unity', 'mutual attraction', 'connection'], reversed: ['imbalance', 'broken partnership', 'disharmony', 'mistrust'] },
    planet: 'Venus', zodiacSign: 'Cancer',
    description: 'A man and woman exchange cups in a pledge, a winged lion head above a caduceus between them.',
    imageSlug: 'two-of-cups',
  },
  {
    id: 'three-of-cups', name: 'Three of Cups', arcana: 'minor', suit: 'cups', number: 3, element: 'water',
    keywords: { upright: ['celebration', 'friendship', 'community', 'joy'], reversed: ['overindulgence', 'gossip', 'isolation', 'exclusion'] },
    planet: 'Mercury', zodiacSign: 'Cancer',
    description: 'Three women dance in a circle raising cups high in a toast, surrounded by harvest fruits and flowers.',
    imageSlug: 'three-of-cups',
  },
  {
    id: 'four-of-cups', name: 'Four of Cups', arcana: 'minor', suit: 'cups', number: 4, element: 'water',
    keywords: { upright: ['contemplation', 'apathy', 'reevaluation', 'meditation'], reversed: ['awareness', 'new possibility', 'acceptance', 'motivation'] },
    planet: 'Moon', zodiacSign: 'Cancer',
    description: 'A young man sits cross-legged beneath a tree, staring at three cups while a hand extends a fourth from a cloud, which he ignores.',
    imageSlug: 'four-of-cups',
  },
  {
    id: 'five-of-cups', name: 'Five of Cups', arcana: 'minor', suit: 'cups', number: 5, element: 'water',
    keywords: { upright: ['loss', 'grief', 'regret', 'disappointment'], reversed: ['acceptance', 'moving on', 'forgiveness', 'recovery'] },
    planet: 'Mars', zodiacSign: 'Scorpio',
    description: 'A cloaked figure mourns three spilled cups, two upright cups behind unnoticed, a bridge to a distant castle across a river.',
    imageSlug: 'five-of-cups',
  },
  {
    id: 'six-of-cups', name: 'Six of Cups', arcana: 'minor', suit: 'cups', number: 6, element: 'water',
    keywords: { upright: ['nostalgia', 'childhood memories', 'innocence', 'reunion'], reversed: ['stuck in the past', 'unrealistic', 'naivety', 'moving forward'] },
    planet: 'Sun', zodiacSign: 'Scorpio',
    description: 'A young boy offers a cup of flowers to a smaller girl in a village garden, six cups brimming with white blossoms.',
    imageSlug: 'six-of-cups',
  },
  {
    id: 'seven-of-cups', name: 'Seven of Cups', arcana: 'minor', suit: 'cups', number: 7, element: 'water',
    keywords: { upright: ['fantasy', 'illusion', 'choices', 'wishful thinking'], reversed: ['clarity', 'focus', 'determination', 'reality check'] },
    planet: 'Venus', zodiacSign: 'Scorpio',
    description: 'A silhouette gazes at seven cups in clouds, each with a vision: castle, jewels, wreath, dragon, veiled figure, snake, glowing head.',
    imageSlug: 'seven-of-cups',
  },
  {
    id: 'eight-of-cups', name: 'Eight of Cups', arcana: 'minor', suit: 'cups', number: 8, element: 'water',
    keywords: { upright: ['walking away', 'disillusionment', 'seeking truth', 'letting go'], reversed: ['fear of change', 'avoidance', 'clinging', 'stagnation'] },
    planet: 'Saturn', zodiacSign: 'Pisces',
    description: 'A cloaked figure walks away from eight stacked cups toward mountains under a crescent moon, leaving behind what was once valued.',
    imageSlug: 'eight-of-cups',
  },
  {
    id: 'nine-of-cups', name: 'Nine of Cups', arcana: 'minor', suit: 'cups', number: 9, element: 'water',
    keywords: { upright: ['contentment', 'satisfaction', 'wish fulfillment', 'gratitude'], reversed: ['dissatisfaction', 'greed', 'materialism', 'unfulfilled wishes'] },
    planet: 'Jupiter', zodiacSign: 'Pisces',
    description: 'A satisfied figure sits with arms crossed before a curved shelf of nine golden cups, beaming with pride.',
    imageSlug: 'nine-of-cups',
  },
  {
    id: 'ten-of-cups', name: 'Ten of Cups', arcana: 'minor', suit: 'cups', number: 10, element: 'water',
    keywords: { upright: ['happiness', 'family harmony', 'fulfillment', 'emotional bliss'], reversed: ['broken family', 'disharmony', 'misaligned values', 'shattered dreams'] },
    planet: 'Mars', zodiacSign: 'Pisces',
    description: 'A joyful couple raises arms toward a rainbow of ten cups, children play nearby beside a home and flowing stream.',
    imageSlug: 'ten-of-cups',
  },
  {
    id: 'page-of-cups', name: 'Page of Cups', arcana: 'minor', suit: 'cups', number: 11, element: 'water',
    keywords: { upright: ['creative opportunity', 'curiosity', 'intuitive message', 'dreamer'], reversed: ['emotional immaturity', 'escapism', 'creative block', 'insecurity'] },
    description: 'A young person in a floral tunic stands by the sea, surprised at a fish popping from their golden cup.',
    imageSlug: 'page-of-cups',
  },
  {
    id: 'knight-of-cups', name: 'Knight of Cups', arcana: 'minor', suit: 'cups', number: 12, element: 'water',
    keywords: { upright: ['romance', 'charm', 'imagination', 'idealism'], reversed: ['moodiness', 'unrealistic expectations', 'jealousy', 'disappointment'] },
    description: 'A knight rides a calm white horse extending a golden cup forward, a peaceful river winding through the landscape behind.',
    imageSlug: 'knight-of-cups',
  },
  {
    id: 'queen-of-cups', name: 'Queen of Cups', arcana: 'minor', suit: 'cups', number: 13, element: 'water',
    keywords: { upright: ['compassion', 'emotional depth', 'intuition', 'nurturing'], reversed: ['codependence', 'emotional manipulation', 'insecurity', 'martyrdom'] },
    description: 'A contemplative queen sits at the water\'s edge gazing into an elaborate closed chalice, cherubs carved into her throne.',
    imageSlug: 'queen-of-cups',
  },
  {
    id: 'king-of-cups', name: 'King of Cups', arcana: 'minor', suit: 'cups', number: 14, element: 'water',
    keywords: { upright: ['emotional maturity', 'diplomacy', 'balance', 'wisdom'], reversed: ['emotional manipulation', 'volatility', 'coldness', 'moodiness'] },
    description: 'A king sits on a throne floating on turbulent sea, holding a cup and scepter, calm despite crashing waves and a ship behind.',
    imageSlug: 'king-of-cups',
  },

  // ============================================================
  // MINOR ARCANA - SWORDS (Air)
  // ============================================================
  {
    id: 'ace-of-swords', name: 'Ace of Swords', arcana: 'minor', suit: 'swords', number: 1, element: 'air',
    keywords: { upright: ['clarity', 'breakthrough', 'truth', 'new idea'], reversed: ['confusion', 'misinformation', 'brutality', 'chaos'] },
    description: 'A hand from a cloud grips an upright sword crowned with a golden wreath, jagged mountains and grey sky behind.',
    imageSlug: 'ace-of-swords',
  },
  {
    id: 'two-of-swords', name: 'Two of Swords', arcana: 'minor', suit: 'swords', number: 2, element: 'air',
    keywords: { upright: ['indecision', 'stalemate', 'difficult choices', 'avoidance'], reversed: ['information overload', 'lesser of two evils', 'clarity', 'emotional turmoil'] },
    planet: 'Moon', zodiacSign: 'Libra',
    description: 'A blindfolded woman sits by the sea holding two crossed swords on her shoulders, a crescent moon above calm waters.',
    imageSlug: 'two-of-swords',
  },
  {
    id: 'three-of-swords', name: 'Three of Swords', arcana: 'minor', suit: 'swords', number: 3, element: 'air',
    keywords: { upright: ['heartbreak', 'sorrow', 'grief', 'painful truth'], reversed: ['recovery', 'forgiveness', 'releasing pain', 'optimism'] },
    planet: 'Saturn', zodiacSign: 'Libra',
    description: 'Three swords pierce a red heart amid heavy rain clouds and downpour.',
    imageSlug: 'three-of-swords',
  },
  {
    id: 'four-of-swords', name: 'Four of Swords', arcana: 'minor', suit: 'swords', number: 4, element: 'air',
    keywords: { upright: ['rest', 'restoration', 'contemplation', 'recovery'], reversed: ['restlessness', 'burnout', 'stagnation', 'inability to rest'] },
    planet: 'Jupiter', zodiacSign: 'Libra',
    description: 'A knight effigy lies on a tomb in a quiet church, three swords on the wall, stained-glass casting colored light.',
    imageSlug: 'four-of-swords',
  },
  {
    id: 'five-of-swords', name: 'Five of Swords', arcana: 'minor', suit: 'swords', number: 5, element: 'air',
    keywords: { upright: ['conflict', 'defeat', 'hollow victory', 'hostility'], reversed: ['reconciliation', 'making amends', 'moving past conflict', 'forgiveness'] },
    planet: 'Venus', zodiacSign: 'Aquarius',
    description: 'A smirking figure holds three swords, two on the ground, two defeated figures walk away toward stormy sky.',
    imageSlug: 'five-of-swords',
  },
  {
    id: 'six-of-swords', name: 'Six of Swords', arcana: 'minor', suit: 'swords', number: 6, element: 'air',
    keywords: { upright: ['transition', 'moving on', 'leaving behind', 'healing'], reversed: ['resistance to change', 'unfinished business', 'baggage', 'stuckness'] },
    planet: 'Mercury', zodiacSign: 'Aquarius',
    description: 'A ferryman guides a woman and child across calm waters, six swords upright in the bow, toward a peaceful shore.',
    imageSlug: 'six-of-swords',
  },
  {
    id: 'seven-of-swords', name: 'Seven of Swords', arcana: 'minor', suit: 'swords', number: 7, element: 'air',
    keywords: { upright: ['deception', 'strategy', 'stealth', 'cunning'], reversed: ['confession', 'coming clean', 'conscience', 'getting caught'] },
    planet: 'Moon', zodiacSign: 'Aquarius',
    description: 'A figure tiptoes from a military camp carrying five swords, glancing back with a sly grin, two swords left behind.',
    imageSlug: 'seven-of-swords',
  },
  {
    id: 'eight-of-swords', name: 'Eight of Swords', arcana: 'minor', suit: 'swords', number: 8, element: 'air',
    keywords: { upright: ['restriction', 'imprisonment', 'helplessness', 'self-imposed limitation'], reversed: ['freedom', 'release', 'new perspective', 'self-acceptance'] },
    planet: 'Jupiter', zodiacSign: 'Gemini',
    description: 'A blindfolded, loosely bound woman stands among eight swords in muddy ground, a castle on a distant cliff.',
    imageSlug: 'eight-of-swords',
  },
  {
    id: 'nine-of-swords', name: 'Nine of Swords', arcana: 'minor', suit: 'swords', number: 9, element: 'air',
    keywords: { upright: ['anxiety', 'nightmares', 'worry', 'despair'], reversed: ['hope', 'recovery', 'reaching out', 'release of anxiety'] },
    planet: 'Mars', zodiacSign: 'Gemini',
    description: 'A figure sits upright in bed, face in hands, nine swords on the dark wall behind, a quilt of roses below.',
    imageSlug: 'nine-of-swords',
  },
  {
    id: 'ten-of-swords', name: 'Ten of Swords', arcana: 'minor', suit: 'swords', number: 10, element: 'air',
    keywords: { upright: ['painful ending', 'rock bottom', 'betrayal', 'finality'], reversed: ['recovery', 'regeneration', 'worst is over', 'resilience'] },
    planet: 'Sun', zodiacSign: 'Gemini',
    description: 'A figure lies face down with ten swords in their back under a black sky, golden dawn breaking on the distant horizon.',
    imageSlug: 'ten-of-swords',
  },
  {
    id: 'page-of-swords', name: 'Page of Swords', arcana: 'minor', suit: 'swords', number: 11, element: 'air',
    keywords: { upright: ['curiosity', 'mental agility', 'new ideas', 'vigilance'], reversed: ['deception', 'manipulation', 'all talk no action', 'cynicism'] },
    description: 'A young figure on a windswept hill holds a sword aloft, hair and clouds blowing, looking over their shoulder alertly.',
    imageSlug: 'page-of-swords',
  },
  {
    id: 'knight-of-swords', name: 'Knight of Swords', arcana: 'minor', suit: 'swords', number: 12, element: 'air',
    keywords: { upright: ['ambition', 'fast thinking', 'assertive action', 'driven'], reversed: ['restlessness', 'recklessness', 'aggression', 'burnout'] },
    description: 'An armored knight charges on a galloping white horse, sword raised, cloak flying against torn racing clouds.',
    imageSlug: 'knight-of-swords',
  },
  {
    id: 'queen-of-swords', name: 'Queen of Swords', arcana: 'minor', suit: 'swords', number: 13, element: 'air',
    keywords: { upright: ['clear thinking', 'direct communication', 'independence', 'perceptiveness'], reversed: ['cold-heartedness', 'bitterness', 'overly critical', 'isolation'] },
    description: 'A stern queen on a stone throne with butterflies, holding an upright sword, extending her left hand toward the viewer.',
    imageSlug: 'queen-of-swords',
  },
  {
    id: 'king-of-swords', name: 'King of Swords', arcana: 'minor', suit: 'swords', number: 14, element: 'air',
    keywords: { upright: ['intellectual authority', 'truth', 'clear judgment', 'ethical leadership'], reversed: ['tyranny', 'abuse of power', 'manipulation', 'cruelty'] },
    description: 'A king on a high throne with butterflies and moons, holding a slightly tilted sword, trees bending in wind behind.',
    imageSlug: 'king-of-swords',
  },

  // ============================================================
  // MINOR ARCANA - PENTACLES (Earth)
  // ============================================================
  {
    id: 'ace-of-pentacles', name: 'Ace of Pentacles', arcana: 'minor', suit: 'pentacles', number: 1, element: 'earth',
    keywords: { upright: ['new opportunity', 'prosperity', 'abundance', 'security'], reversed: ['missed chance', 'scarcity', 'instability', 'poor planning'] },
    description: 'A hand from a cloud offers a large golden pentacle above a lush garden with hedges leading to distant mountains.',
    imageSlug: 'ace-of-pentacles',
  },
  {
    id: 'two-of-pentacles', name: 'Two of Pentacles', arcana: 'minor', suit: 'pentacles', number: 2, element: 'earth',
    keywords: { upright: ['balance', 'adaptability', 'juggling priorities', 'flexibility'], reversed: ['imbalance', 'overwhelm', 'disorganization', 'overextended'] },
    planet: 'Jupiter', zodiacSign: 'Capricorn',
    description: 'A young man dances juggling two pentacles connected by an infinity ribbon, ships riding waves behind.',
    imageSlug: 'two-of-pentacles',
  },
  {
    id: 'three-of-pentacles', name: 'Three of Pentacles', arcana: 'minor', suit: 'pentacles', number: 3, element: 'earth',
    keywords: { upright: ['teamwork', 'craftsmanship', 'collaboration', 'mastery'], reversed: ['lack of teamwork', 'mediocrity', 'misalignment', 'poor quality'] },
    planet: 'Mars', zodiacSign: 'Capricorn',
    description: 'A stonemason works on a cathedral arch while a monk and nobleman consult plans, three pentacles carved above.',
    imageSlug: 'three-of-pentacles',
  },
  {
    id: 'four-of-pentacles', name: 'Four of Pentacles', arcana: 'minor', suit: 'pentacles', number: 4, element: 'earth',
    keywords: { upright: ['security', 'conservation', 'control', 'possessiveness'], reversed: ['greed', 'materialism', 'letting go', 'financial insecurity'] },
    planet: 'Sun', zodiacSign: 'Capricorn',
    description: 'A figure clutches a pentacle to their chest, one under each foot and one on head, sitting outside a city.',
    imageSlug: 'four-of-pentacles',
  },
  {
    id: 'five-of-pentacles', name: 'Five of Pentacles', arcana: 'minor', suit: 'pentacles', number: 5, element: 'earth',
    keywords: { upright: ['hardship', 'loss', 'poverty', 'isolation'], reversed: ['recovery', 'improvement', 'turning a corner', 'finding help'] },
    planet: 'Mercury', zodiacSign: 'Taurus',
    description: 'Two impoverished figures trudge through snow past a lit stained-glass church window with five pentacles.',
    imageSlug: 'five-of-pentacles',
  },
  {
    id: 'six-of-pentacles', name: 'Six of Pentacles', arcana: 'minor', suit: 'pentacles', number: 6, element: 'earth',
    keywords: { upright: ['generosity', 'charity', 'giving and receiving', 'fairness'], reversed: ['strings attached', 'inequality', 'debt', 'selfishness'] },
    planet: 'Moon', zodiacSign: 'Taurus',
    description: 'A merchant holds balanced scales while giving coins to two kneeling beggars, six pentacles floating around.',
    imageSlug: 'six-of-pentacles',
  },
  {
    id: 'seven-of-pentacles', name: 'Seven of Pentacles', arcana: 'minor', suit: 'pentacles', number: 7, element: 'earth',
    keywords: { upright: ['patience', 'long-term investment', 'assessment', 'perseverance'], reversed: ['impatience', 'wasted effort', 'lack of reward', 'frustration'] },
    planet: 'Saturn', zodiacSign: 'Taurus',
    description: 'A farmer leans on a hoe gazing at a vine bearing seven golden pentacles, contemplating the harvest.',
    imageSlug: 'seven-of-pentacles',
  },
  {
    id: 'eight-of-pentacles', name: 'Eight of Pentacles', arcana: 'minor', suit: 'pentacles', number: 8, element: 'earth',
    keywords: { upright: ['skill development', 'diligence', 'dedication', 'craftsmanship'], reversed: ['perfectionism', 'lack of focus', 'shortcuts', 'mediocrity'] },
    planet: 'Sun', zodiacSign: 'Virgo',
    description: 'A craftsman chisels a pentacle at a workbench, six completed on a post and one at his feet, a town in the distance.',
    imageSlug: 'eight-of-pentacles',
  },
  {
    id: 'nine-of-pentacles', name: 'Nine of Pentacles', arcana: 'minor', suit: 'pentacles', number: 9, element: 'earth',
    keywords: { upright: ['self-sufficiency', 'luxury', 'financial independence', 'accomplishment'], reversed: ['overinvestment', 'superficiality', 'reckless spending', 'dependence'] },
    planet: 'Venus', zodiacSign: 'Virgo',
    description: 'An elegant woman stands in a vineyard with nine pentacles, a hooded falcon on her glove, a grand estate behind.',
    imageSlug: 'nine-of-pentacles',
  },
  {
    id: 'ten-of-pentacles', name: 'Ten of Pentacles', arcana: 'minor', suit: 'pentacles', number: 10, element: 'earth',
    keywords: { upright: ['legacy', 'wealth', 'family', 'long-term success'], reversed: ['financial failure', 'family disputes', 'loss of legacy', 'instability'] },
    planet: 'Mercury', zodiacSign: 'Virgo',
    description: 'An elderly patriarch sits beneath an archway with dogs, watching family in a courtyard, ten pentacles in the Tree of Life pattern.',
    imageSlug: 'ten-of-pentacles',
  },
  {
    id: 'page-of-pentacles', name: 'Page of Pentacles', arcana: 'minor', suit: 'pentacles', number: 11, element: 'earth',
    keywords: { upright: ['ambition', 'studiousness', 'new venture', 'desire to learn'], reversed: ['lack of progress', 'procrastination', 'unfocused', 'unrealistic goals'] },
    description: 'A young figure in a green field holds a pentacle with both hands, gazing intently, mountains and plowed earth behind.',
    imageSlug: 'page-of-pentacles',
  },
  {
    id: 'knight-of-pentacles', name: 'Knight of Pentacles', arcana: 'minor', suit: 'pentacles', number: 12, element: 'earth',
    keywords: { upright: ['reliability', 'hard work', 'efficiency', 'methodical progress'], reversed: ['stubbornness', 'laziness', 'boredom', 'perfectionism'] },
    description: 'A patient knight atop a steady dark horse in a plowed field, holding a pentacle with quiet determination, oak leaves on helmet.',
    imageSlug: 'knight-of-pentacles',
  },
  {
    id: 'queen-of-pentacles', name: 'Queen of Pentacles', arcana: 'minor', suit: 'pentacles', number: 13, element: 'earth',
    keywords: { upright: ['abundance', 'practicality', 'nurturing', 'financial security'], reversed: ['self-centeredness', 'jealousy', 'smothering', 'work-life imbalance'] },
    description: 'A serene queen on a throne with fruit and goats, cradling a pentacle amid roses and a lush garden, a rabbit at her feet.',
    imageSlug: 'queen-of-pentacles',
  },
  {
    id: 'king-of-pentacles', name: 'King of Pentacles', arcana: 'minor', suit: 'pentacles', number: 14, element: 'earth',
    keywords: { upright: ['wealth', 'business acumen', 'discipline', 'provider'], reversed: ['greed', 'materialism', 'financial mismanagement', 'stubbornness'] },
    description: 'A prosperous king on a throne with bull carvings, foot on a bull head, holding scepter and pentacle, vines and castle behind.',
    imageSlug: 'king-of-pentacles',
  },
]
